// src/components/ImageUpload.jsx

import React, { useState } from "react";
import {
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import axios from "axios";

const ImageUpload = ({
  docId,
  uploadedImages,
  setUploadedImages,
  isEditable,
  handleRemoveImage,
}) => {
  const [loading, setLoading] = useState(false);

  const handleImageUpload = async (file) => {
    if (!file) {
      console.warn("No file received");
      return;
    }

    console.log("File received:", file);
    console.log("File name:", file.name);
    console.log("File type:", file.type);
    console.log("File size:", file.size);

    const allowedExtensions = [
      ".jpeg",
      ".jpg",
      ".png",
      ".gif",
      ".pdf",
      ".webp",
    ];
    const allowedMimeTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
      "image/webp",
    ];

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const isExtensionAllowed = allowedExtensions.includes(fileExtension);
    const isMimeTypeAllowed = file.type
      ? allowedMimeTypes.includes(file.type.toLowerCase())
      : true;

    if (!isExtensionAllowed || !isMimeTypeAllowed) {
      alert(
        "Unsupported file type. Please upload a JPEG, PNG, GIF, or PDF file.",
      );
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      // 10MB limit
      alert("File size exceeds the 10MB limit.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append(`documents[${docId}]`, file, file.name);

      const response = await axios.post(
        `https://formbuilderapi.getforsite.com/api/forms/upload-documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const uploadedUrl = response.data.uploadedUrls[docId];
      setUploadedImages((prevImages) => [...prevImages, uploadedUrl]);
      alert("Image uploaded successfully.");
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("An error occurred while uploading the image.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Button
        variant="outlined"
        startIcon={<CloudUploadIcon />}
        component="label"
        disabled={!isEditable || loading}
        sx={{ marginTop: 1 }}
      >
        Upload Image
        <input
          type="file"
          hidden
          accept="image/*,application/pdf"
          onChange={(e) => handleImageUpload(e.target.files[0])}
        />
      </Button>
      {loading && <Typography variant="body2">Uploading...</Typography>}
      {uploadedImages.length > 0 && (
        <ImageList cols={3} gap={8} sx={{ marginTop: 1 }}>
          {uploadedImages.map((imgUrl, imgIndex) => (
            <ImageListItem key={imgIndex}>
              <img
                src={imgUrl}
                alt={`Uploaded ${imgIndex + 1}`}
                loading="lazy"
                style={{ borderRadius: 8 }}
              />
              {isEditable && (
                <ImageListItemBar
                  position="top"
                  actionIcon={
                    <IconButton
                      sx={{ color: "white" }}
                      aria-label={`delete image ${imgIndex + 1}`}
                      onClick={() => handleRemoveImage(imgIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                  actionPosition="right"
                />
              )}
            </ImageListItem>
          ))}
        </ImageList>
      )}
    </div>
  );
};

ImageUpload.propTypes = {
  docId: PropTypes.string.isRequired,
  uploadedImages: PropTypes.arrayOf(PropTypes.string).isRequired,
  setUploadedImages: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  handleRemoveImage: PropTypes.func.isRequired,
};

export default ImageUpload;
